import type InputType from "@tosize/shapes/src/lib/models/Input/InputType";
import { getMaxBasedOnUnit } from "@tosize/shapes/src/lib/utils";
import type { Input } from "@tosize/shapes/src/types/shape";
import { Alert, NativeNumberInput, Switch, useNotifications } from "@tosize/ui";
import { Fragment, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { match } from "ts-pattern";
import { useConfig } from "../../../../context/ConfigContext";
import { useErrorContext } from "../../../../context/ErrorContext";
import { usePostDataContext } from "../../../../context/PostDataContext";
import useDimensionsForm from "../../../../hooks/useDimensionsForm";
import useShapes from "../../../../hooks/useShapes";
import { VisualisationDialog } from "../../Fields/VisualisationDialog";
import FinishWarning from "../../Finishes/FinishWarning";

const DimensionsForm = () => {
	const intl = useIntl();
	const { shape } = useShapes();
	const { setWorkpiece, workpiece } = usePostDataContext();
	const { errors } = useErrorContext();
	const { shapeModel } = useDimensionsForm(shape);

	const [nonCombinableFields, combinableFields] = useMemo(
		() => [
			shape?.inputs.filter((input) => !input.relativeFields) ?? [],
			shape?.inputs.filter((input) => !!input.relativeFields) ?? [],
		],
		[shape?.inputs],
	);

	const combinableData = useMemo(() => {
		const ids = Object.values(combinableFields).map((field) => String(field.id));

		return Object.entries(workpiece.shape_dimensions).filter(([key]) => ids.includes(key));
	}, [combinableFields, workpiece.shape_dimensions]);

	const [isChecked, setIsChecked] = useState<boolean>(
		combinableData.every(([_key, value]) => value === combinableData[0]?.[1]),
	);

	/**
	 * Checks if any combinable fields are filled
	 * and if all combinable fields are equal
	 * and then returns that value
	 */
	const relatedFieldData = useMemo(() => {
		const filteredData = combinableData.filter(([_key, value]) => Number(value));

		const isSame = filteredData.length
			? filteredData.every(([_key, value]) => value === filteredData[0]?.[1])
			: false;

		const dynamicConstraints =
			combinableFields[0] &&
			shapeModel?.getConstraints(workpiece.shape_dimensions, combinableFields[0], true);

		if (
			isSame &&
			filteredData[0] &&
			Number(filteredData[0][1]) <= (dynamicConstraints ?? Number(combinableFields[0]?.max))
		) {
			return filteredData[0]?.[1];
		}
	}, [combinableData, combinableFields, shapeModel, workpiece.shape_dimensions]);

	const toggleCornerSwitch = useCallback(
		(checked: boolean) => {
			// Apply relatedFieldData on all corners if available
			// if not values will be reset
			const relatedFields = combinableFields.reduce(
				(prev, current) =>
					Object.assign(prev, {
						[current.id]: checked && relatedFieldData ? relatedFieldData : "",
					}),
				{} as InputType,
			);

			setWorkpiece((prev) => ({
				...prev,
				shape_dimensions: { ...prev.shape_dimensions, ...relatedFields },
			}));

			setIsChecked(checked);
		},
		[combinableFields, relatedFieldData, setWorkpiece],
	);

	const [hasErrors, setErrors] = useState(false);

	useEffect(() => {
		if (errors.dimensions && nonCombinableFields) {
			setErrors(
				Object.keys(errors.dimensions).some((error) =>
					nonCombinableFields.some((field) => field.id === error),
				),
			);
		}
	}, [errors.dimensions, nonCombinableFields]);

	return (
		<Fragment>
			{shape && !nonCombinableFields.length && !combinableFields.length && (
				<Alert variant="info">
					<FormattedMessage defaultMessage="Voor deze vorm kunnen geen afmetingen worden opgegeven" />
				</Alert>
			)}
			<div>
				{!!nonCombinableFields.length && (
					<div className="grid grid-cols-2 gap-2 last:odd:child:col-span-2 sm:gap-4 lg:gap-2 xl:gap-4">
						{nonCombinableFields.map((input) => (
							<DimensionsInput
								key={input.id}
								input={input}
								equalFields={isChecked}
								correspondingFields={combinableFields}
							/>
						))}
					</div>
				)}

				{!!combinableFields.length && (
					<>
						<div className="my-4">
							<Switch
								checked={isChecked}
								onCheckedChange={toggleCornerSwitch}
								id="corners-toggle"
								label={intl.formatMessage({
									defaultMessage: "Alle hoeken gelijk",
								})}
								disabled={hasErrors}
							/>
						</div>

						{hasErrors && !isChecked && (
							<FinishWarning>
								<FormattedMessage defaultMessage="Vul eerst bovenstaande afmetingen correct in" />
							</FinishWarning>
						)}

						<div className="grid grid-cols-2 gap-2 last:odd:child:col-span-2 sm:gap-4 lg:gap-2 xl:gap-4">
							{!isChecked ? (
								combinableFields.map((input) => (
									<DimensionsInput
										key={input.id}
										input={input}
										disabled={hasErrors}
										equalFields={false}
										correspondingFields={nonCombinableFields}
									/>
								))
							) : (
								<div>
									{combinableFields[0] && (
										<DimensionsInput
											input={combinableFields[0]}
											disabled={hasErrors}
											equalFields={true}
											correspondingFields={nonCombinableFields}
										/>
									)}
									{hasErrors && (
										<div className="flex items-center text-sm text-warning">
											<FormattedMessage
												defaultMessage="Vul eerst {fields} correct in"
												id="aXxjYG"
												values={{
													fields: intl.formatList(
														nonCombinableFields.map((field) => field.label),
														{ type: "conjunction" },
													),
												}}
											/>
										</div>
									)}
								</div>
							)}
						</div>
					</>
				)}

				<Suspense fallback={null}>
					<div className="mt-4 text-sm lg:mt-2">
						<VisualisationDialog />
					</div>
				</Suspense>
			</div>
		</Fragment>
	);
};

export default DimensionsForm;

interface DimensionsInputProps {
	input: Input;
	correspondingFields?: Input[];
	disabled?: boolean;
	equalFields?: boolean;
}

function DimensionsInput(props: DimensionsInputProps) {
	const { input, correspondingFields, disabled, equalFields } = props;

	const intl = useIntl();
	const { notify, remove } = useNotifications();
	const { mode } = useConfig();
	const { errors, touchedFields } = useErrorContext();
	const { workpiece, setWorkpiece, data, setCanvasSettings } = usePostDataContext();
	const { shape } = useShapes();
	const { shapeModel, validate } = useDimensionsForm(shape);

	const isCombinedField = useMemo(
		() => !!equalFields && !!input.relativeFields?.length,
		[equalFields, input.relativeFields?.length],
	);

	const inputLabel = useMemo(() => {
		return isCombinedField ? intl.formatMessage({ defaultMessage: "alle hoeken" }) : input.label;
	}, [input.label, intl, isCombinedField]);

	const valueModifier = match(input.precision)
		.with(2, () => 100)
		.with(1, () => 10)
		.otherwise(() => 1);

	const value = workpiece.shape_dimensions[input.id]
		? Number(workpiece.shape_dimensions[input.id]) / valueModifier
		: undefined;

	const dynamicConstraints =
		shapeModel?.getConstraints(workpiece.shape_dimensions, input, isCombinedField) ?? null;

	const min = input.min / valueModifier;
	const unitMax = getMaxBasedOnUnit(input.max, data.unit);

	const max =
		unitMax !== undefined
			? (dynamicConstraints ? Math.min(dynamicConstraints, unitMax) : unitMax) / valueModifier
			: undefined;

	const correspondingValues = correspondingFields
		?.map((field) => workpiece.shape_dimensions[field.id])
		.filter(Number);

	const surfix = match(input.unit)
		.with("", () => undefined)
		.with(undefined, () => "cm")
		.otherwise(() => input.unit);

	async function valueChangeHandler(val: number) {
		const value = val * valueModifier;
		let extraData = {};

		if (isCombinedField && input.relativeFields) {
			// When the equal corners checkbox is checked and the input has relative
			// fields, update all the relative fields to the current input value
			extraData = input.relativeFields.reduce(
				(prev, current) => Object.assign(prev, { [current]: value }),
				{},
			);
		}

		// When the component has corresponding fields, that are also combinable fields,
		//  check for each field if it's value is stil valid.
		// If value is higher than max, set to max.
		const correspondingRelativeFields = correspondingFields
			?.filter((field) => !!field.relativeFields?.length)
			.map((field) => {
				const constraints = shapeModel?.getConstraints(
					{ ...workpiece.shape_dimensions, [input.id]: value },
					field,
					equalFields,
				);

				const max =
					constraints !== undefined ? constraints : getMaxBasedOnUnit(field.max, data.unit);

				if (
					!!data.unit &&
					max !== undefined &&
					Number(workpiece.shape_dimensions[field.id]) > max
				) {
					return {
						...field,
						max: typeof field.max === "number" ? max : { [data.unit]: max, ...field.max },
					};
				}
			})
			.filter(Boolean) as Input[];

		if (correspondingRelativeFields.length) {
			for (const field of correspondingRelativeFields) {
				extraData = { ...extraData, [field.id]: field.max };
			}

			const identifier = correspondingRelativeFields.length;

			remove(identifier);

			// wait for the previous notification to be removed
			await new Promise((resolve) => setTimeout(resolve, 100));

			notify({
				identifier,
				title: intl.formatMessage(
					{
						defaultMessage:
							"De {length, plural, one {waarde van {value} is} other {waardes van {value} zijn}} aangepast omdat de maximale waarde is overschreden",
					},
					{
						length: correspondingRelativeFields.length,
						value: intl.formatList(
							correspondingRelativeFields.map((field) => `${field.optionLabel} ${field.label}`),
							{ type: "conjunction" },
						),
					},
				),
				variant: "warning",
			});
		}

		setWorkpiece((prev) => ({
			...prev,
			shape_dimensions: {
				...prev.shape_dimensions,
				[input.id]: value ? value : "",
				...extraData,
			},
		}));
	}

	return (
		<div>
			<NativeNumberInput
				tooltip={input.tooltip}
				title={input.optionLabel ? `${input.optionLabel} ${inputLabel}` : inputLabel}
				id={input.id}
				min={min}
				max={max}
				surfix={surfix}
				decimals={input.precision}
				disabled={disabled || (!!correspondingValues?.length && max === 0)}
				isInvalid={!!errors.dimensions[input.id]?.length}
				onFocus={() =>
					setCanvasSettings({
						view: "2d",
						field: isCombinedField ? `${input.id.split("_").at(0)}_combined` : input.id,
					})
				}
				onBlur={async (e) => {
					setCanvasSettings(undefined);
					if (mode === "editCartItem" || mode === "editProjectItem") {
						await valueChangeHandler(Number(e.target.value));
					}
				}}
				value={value}
				valueChangeHandler={async (val) => {
					if (mode !== "editCartItem" && mode !== "editProjectItem") {
						return await valueChangeHandler(val);
					}
				}}
				changeHandlerType="forceValidation"
				customValidation={async (val) => {
					const fields = [...touchedFields.dimensions, input.id];
					const values = {
						...Object.entries(workpiece.shape_dimensions).reduce(
							(prev, [key, value]) =>
								Object.assign(prev, { [key]: Number(value) === 0 ? "" : value }),
							{},
						),
						[input.id]: val === 0 ? "" : val * valueModifier,
					};

					return await validate({ fields, values });
				}}
			/>

			{errors.dimensions[input.id]?.map((issue, index) => (
				<span className="flex items-center text-danger text-sm" key={index}>
					{issue.message}
				</span>
			))}
		</div>
	);
}
